import { useEffect, useState, type KeyboardEvent } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

const HEX_COLOR = /^#[0-9A-F]{6}$/i;

interface ColorFieldProps {
  id: string;
  label: string;
  description: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  suggestions?: string[];
}

export function ColorField({
  id,
  label,
  description,
  value,
  onChange,
  disabled,
  suggestions,
}: ColorFieldProps) {
  const [hexDraft, setHexDraft] = useState(value);

  useEffect(() => setHexDraft(value), [value]);

  function commit() {
    if (HEX_COLOR.test(hexDraft)) {
      onChange(hexDraft.toUpperCase());
    } else {
      setHexDraft(value);
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      commit();
      event.currentTarget.blur();
    }
    if (event.key === "Escape") {
      setHexDraft(value);
      event.currentTarget.blur();
    }
  }

  return (
    <div className="flex flex-col gap-3 border-b border-studio-border py-4 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <label htmlFor={`${id}-hex`} className="text-sm font-semibold text-studio-ink">
          {label}
        </label>
        <p className="mt-0.5 text-xs leading-5 text-studio-muted">{description}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {suggestions && (
          <div className="hidden items-center gap-1 lg:flex" aria-label={`${label} suggestions`}>
            {suggestions.map((suggestion) => {
              const active = suggestion.toUpperCase() === value.toUpperCase();
              return (
                <button
                  key={suggestion}
                  type="button"
                  aria-label={`Use ${suggestion}`}
                  aria-pressed={active}
                  disabled={disabled}
                  onClick={() => onChange(suggestion)}
                  className="relative h-8 w-8 rounded-full border border-studio-border transition-transform hover:scale-105 focus-visible:outline-none focus-visible:shadow-glow-sm disabled:opacity-50"
                  style={{ backgroundColor: suggestion }}
                >
                  {active && <Check className="absolute inset-0 m-auto h-3.5 w-3.5 text-white drop-shadow" />}
                </button>
              );
            })}
          </div>
        )}
        <label
          htmlFor={`${id}-picker`}
          className={cn(
            "relative h-10 w-12 overflow-hidden rounded-lg border border-studio-border bg-studio-card focus-within:shadow-glow-sm",
            disabled && "opacity-50"
          )}
          title={`Open ${label.toLowerCase()} palette`}
        >
          <span className="absolute inset-1 rounded-md" style={{ backgroundColor: value }} />
          <input
            id={`${id}-picker`}
            type="color"
            value={value}
            disabled={disabled}
            aria-label={`Choose ${label.toLowerCase()}`}
            onChange={(event) => onChange(event.target.value.toUpperCase())}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
          />
        </label>
        <input
          id={`${id}-hex`}
          value={hexDraft}
          disabled={disabled}
          maxLength={7}
          spellCheck={false}
          aria-invalid={!HEX_COLOR.test(hexDraft)}
          onChange={(event) => setHexDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={handleKeyDown}
          className="h-10 w-24 rounded-lg border border-studio-border bg-studio-card px-2.5 font-mono text-xs uppercase text-studio-ink outline-none focus:border-studio-accent/60 focus:shadow-glow-sm disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>
    </div>
  );
}
